import { Button, List, Stack, Box } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { FC, ReactElement, useCallback } from "react";
import { useSnackbar } from "notistack";
import {
  ApprovedIcon,
  CancelIcon,
  FlameIcon,
  PendingIcon,
} from "../../../../components/MuiIcons";
import { mainTheme } from "../../../../theme/mainTheme";
import { PanelLayout } from "../PanelLayout";
import { LabelText, ValueText } from "./styles";
import { TVerificationRequest } from "../../models";
import { useActivateScreening } from "../../hooks/useActivateScreening";

type ScreeningStatusPresentation = {
  color: string;
  icon: ReactElement;
  explanation?: string;
};

export const screeningStatusModel: Record<
  string,
  ScreeningStatusPresentation
> = {
  valid: {
    color: mainTheme.palette.success.main,
    icon: <ApprovedIcon viewBox="0 0 20 20" />,
  },
  invalid: {
    color: mainTheme.palette.error.main,
    icon: <CancelIcon viewBox="0 0 20 20" />,
  },
  failed: {
    color: mainTheme.palette.error.main,
    icon: <ErrorOutlineIcon />,
    explanation: "Screening could not be started.",
  },
  pending: {
    color: mainTheme.palette.warning.main,
    icon: <PendingIcon />,
  },
  inactive: {
    color: mainTheme.palette.error.main,
    icon: <FlameIcon viewBox="0 0 20 20" />,
  },
};

export function isOngoingMonitoring(status: string) {
  return status === "valid" || status === "invalid";
}

export const DatabaseScreeningPanel: FC<{
  data: TVerificationRequest;
  onRefresh?: () => Promise<void> | void;
  onExternalIdsRefresh?: () => Promise<void>;
}> = ({ data, onRefresh, onExternalIdsRefresh }) => {
  const [, activating, , activate] = useActivateScreening(data?.id);
  const { enqueueSnackbar } = useSnackbar();

  const handleActivate = useCallback(async () => {
    if (!data?.id || activating) {
      return;
    }

    try {
      await activate();
      enqueueSnackbar("Screening activated", { variant: "success" });
      await Promise.all([onExternalIdsRefresh?.(), onRefresh?.()]);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unable to activate screening";
      enqueueSnackbar(message, { variant: "error" });
    }
  }, [
    activate,
    activating,
    data?.id,
    enqueueSnackbar,
    onExternalIdsRefresh,
    onRefresh,
  ]);

  if (!data) return null;

  const status = data.screeningStatus || "pending";
  const model =
    screeningStatusModel[status as keyof typeof screeningStatusModel];
  const color = model?.color;
  const icon = model?.icon;
  const explanation = model?.explanation;

  return (
    <PanelLayout title="Database screening">
      <List>
        <Stack direction="row" spacing={2} alignItems="center">
          <LabelText>Current status</LabelText>
          {data.screeningStatus && (
            <Box
              data-testid="screening-status-value"
              display="flex"
              alignItems="center"
              width="100%"
              flex="9 1 0"
            >
              <ValueText sx={{ color: color }}>
                {icon}
                {data.screeningStatus}
              </ValueText>
              {data.screeningStatus === "inactive" && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleActivate}
                  disabled={activating}
                >
                  Activate
                </Button>
              )}
            </Box>
          )}
        </Stack>
        {explanation && (
          <Stack alignItems="flex-start" direction="row" spacing={2} mt={2}>
            <LabelText>Explanation</LabelText>
            <ValueText sx={{ textTransform: "none" }}>{explanation}</ValueText>
          </Stack>
        )}
        <Stack alignItems="flex-start" direction="row" spacing={2} mt={2}>
          <LabelText>Ongoing monitoring (daily screening):</LabelText>
          <ValueText>
            {isOngoingMonitoring(data.screeningStatus) ? "ON" : "-"}
          </ValueText>
        </Stack>
        {data.declineReason?.length > 0 && (
          <Stack direction="row" spacing={2} mt={2}>
            <LabelText>Decline reason</LabelText>
            <ValueText>
              {data.declineReason
                .map((reason: string) => reason.replace(/_/g, " "))
                .join(", ")}
            </ValueText>
          </Stack>
        )}
      </List>
    </PanelLayout>
  );
};
